import * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import { isGlobalGSplatClipVolume } from '@/plugins/gsplat/clipping/GSplatClipState'
import { SplatData } from '@/plugins/gsplat/core/SplatData'
import { SplatExporter } from '@/plugins/gsplat/export/SplatExporter'
import { guardGSplatExtraLoaderCapability } from '@/plugins/gsplat/GSplatExtraLoaderCapability'
import type {
  GSplatBindingResourceSource,
  GSplatHandleBindingCreateInput,
  GSplatHandleBindingState,
  GSplatResourceLease,
  IGSplatHandleBinding,
  IGSplatHandleBindingFactory,
} from '@/plugins/gsplat/GSplatHandleBinding'
import {
  type GSplatExportSource,
  type GSplatGpuPickCoordinator,
  type GSplatPickSample,
  GSplatSystem,
  type GSplatSystemAssetSnapshot,
  type GSplatSystemConfiguration,
  type GSplatSystemDirector,
  type GSplatSystemHostServices,
  type GSplatSystemRenderSubmission,
  type SplatDataCpuPicker,
} from '@/plugins/gsplat/GSplatSystem'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import {
  type WebGLGSplatGraphicsAdapter,
  WebGLGSplatGraphicsAdapterFactory,
} from '@/plugins/gsplat/graphics/WebGLGSplatGraphicsAdapter'
import { getGSplatUrlPath } from '@/plugins/gsplat/loaders/GSplatHandler'
import { createGS3DManager } from '@/plugins/gsplat/managerRegistry'
import { createWebGLGSplatUnifiedSystem } from '@/plugins/gsplat/system/WebGLGSplatUnifiedComposition'
import { reportGSplatSortingFallbackOnce } from '@/plugins/gsplat/unified/GSplatPipelineTypes'
import type { EngineContext } from '@/runtime/EngineContext'
import type {
  AssetExporter,
  AssetLoader,
  ExportOptions,
  LoadContext,
  PhaseRenderContext,
} from '@/shared/types/assets'
import type { AssetId, ClipVolume, PickQuery } from '@/shared/types/core'
import type {
  GSplatDeviceBudgetDiagnostics,
  GSplatVisualActivitySnapshot,
  IGS3DManager,
  LODConfig,
  LODEventData,
  LODEventType,
  LODStats,
  LoadResult,
} from '@/shared/types/gsplat'
import type { GSplatCapability } from '@/shared/types/gsplatCapability'
import type { ViewFrameContext, ViewId } from '@/shared/types/view'
import { setMaskRecursive } from '@/shared/utils'
import type {
  GaussianSplatExtraExporter,
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
  GaussianSplatPluginOptions,
  GaussianSplatRenderConfig,
  GSplatExtraExporterCapability,
} from './types/PluginTypes'

const DEFAULT_CONFIG: GaussianSplatRenderConfig = Object.freeze({
  sortingMode: 'auto',
  qualityLevel: 'medium',
  maxSplats: 0,
  opacity: 1,
})

/** `maxSplats` 允许 0 表示不限制；其余情况必须是正的安全整数。 */
function validateMaxSplats(value: unknown): void {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) {
    return
  }
  throw new Error(`非法 GSplat maxSplats: ${String(value)}（0 表示不限制）`)
}

const DEFAULT_LAYER_IDS = Object.freeze([0])

const COMPATIBILITY_CAPABILITIES: GSplatCapability = Object.freeze({
  supported: Object.freeze([
    'unified-format-reader',
    'uncompressed-reader',
    'compressed-reader',
    'sog-reader',
    'lod-streaming',
    'multi-view',
    'cpu-sort',
    'cpu-picking',
    'gpu-picking',
    'shader-effects',
    'work-buffer-modifier',
  ] as const),
  gpuCompute: Object.freeze({
    compute: false,
    storageBuffers: false,
    storageTextures: false,
    atomicUint32: false,
    indirectDispatch: false,
    indirectDraw: false,
    maxStorageBufferBindingSize: 0,
    maxBufferSize: 0,
    maxStorageBuffersPerShaderStage: 0,
    maxComputeWorkgroupStorageSize: 0,
    maxComputeInvocationsPerWorkgroup: 0,
    maxComputeWorkgroupSizeX: 0,
    maxComputeWorkgroupsPerDimension: 0,
    radixScratchBytes: 0,
  }),
  gpuPicking: Object.freeze({
    privateTarget: true,
    targetFormats: Object.freeze(['rgba8unorm'] as const),
    asyncReadback: true,
    maxPickIds: 0x00ff_ffff,
  }),
  relighting: Object.freeze({
    privateTarget: false,
    targetFormats: Object.freeze([] as const),
    floatFilterable: false,
    unormFallback: false,
  }),
})

interface CompatibilityManager extends IGS3DManager {
  setClipVolumes?(volumes: ReadonlyArray<ClipVolume>): boolean
  pick?(ray: THREE.Ray, camera: THREE.Camera, options?: Record<string, unknown>): unknown
  getShaderEffect?(nodeId: string): { enabled: boolean } | null
  setSceneOrigin?(nodeId: string, offset: THREE.Vector3 | null): boolean
}

interface CompatibilityResourceState {
  readonly id: AssetId
  readonly boundingBox: THREE.Box3
  readonly numSplats: number
  readonly object3D: THREE.Object3D | null
  readonly cpuData: unknown | null
  readonly resourceKind: GSplatResourceLease['resourceKind']
  readonly streaming: GSplatResourceLease['streaming']
  readonly octreeSemantics: GSplatResourceLease['octreeSemantics']
  readonly release: () => void
  references: number
  disposed: boolean
}

class CompatibilityResourceLease implements GSplatResourceLease {
  readonly generation = 1
  private released = false

  constructor(private readonly state: CompatibilityResourceState) {}

  get resourceIdentity(): object {
    return this.state
  }

  get numSplats(): number {
    return this.state.numSplats
  }

  get boundingBox(): THREE.Box3 {
    return this.state.boundingBox.clone()
  }

  get aabb(): THREE.Box3 {
    return this.boundingBox
  }

  get object3D(): THREE.Object3D | null {
    return this.state.object3D
  }

  get resourceKind(): GSplatResourceLease['resourceKind'] {
    return this.state.resourceKind
  }

  get streaming(): GSplatResourceLease['streaming'] {
    return this.state.streaming
  }

  get octreeSemantics(): GSplatResourceLease['octreeSemantics'] {
    return this.state.octreeSemantics
  }

  getCpuExportData(): unknown | null {
    return this.state.cpuData
  }

  acquire(): GSplatResourceLease {
    if (this.state.disposed) throw new Error('PlayCanvas compatibility Resource 已销毁')
    this.state.references++
    return new CompatibilityResourceLease(this.state)
  }

  dispose(): void {
    if (this.released) return
    this.released = true
    if (this.state.disposed) return
    this.state.references--
    if (this.state.references > 0) return
    this.state.disposed = true
    this.state.release()
  }
}

function createCompatibilityResource(input: {
  id: AssetId
  boundingBox: THREE.Box3
  numSplats: number
  object3D: THREE.Object3D | null
  cpuData: unknown | null
  resourceKind?: GSplatResourceLease['resourceKind']
  streaming?: GSplatResourceLease['streaming']
  octreeSemantics?: GSplatResourceLease['octreeSemantics']
  release: () => void
}): GSplatResourceLease {
  return new CompatibilityResourceLease({
    ...input,
    resourceKind: input.resourceKind ?? 'uncompressed',
    streaming: input.streaming ?? 'static',
    octreeSemantics: input.octreeSemantics ?? 'none',
    references: 1,
    disposed: false,
  })
}

function toBoundingBox(result: LoadResult): THREE.Box3 {
  return new THREE.Box3(
    new THREE.Vector3(result.boundingBox.min.x, result.boundingBox.min.y, result.boundingBox.min.z),
    new THREE.Vector3(result.boundingBox.max.x, result.boundingBox.max.y, result.boundingBox.max.z)
  )
}

function extractObject3D(manager: CompatibilityManager, id: AssetId): THREE.Object3D | null {
  const model = manager.getByNodeId(id)
  let object3D: THREE.Object3D | null = null
  if (model instanceof THREE.Object3D) {
    object3D = model
  } else if (typeof model === 'object' && model !== null && 'rootNode' in model) {
    const rootNode = (model as { rootNode?: unknown }).rootNode
    if (rootNode instanceof THREE.Object3D) object3D = rootNode
  }
  object3D?.removeFromParent()
  return object3D
}

function getCpuData(manager: CompatibilityManager, id: AssetId): unknown | null {
  const model = manager.getByNodeId(id) as { splatData?: unknown } | undefined
  return model?.splatData ?? null
}

class CompatibilityConfiguration implements GSplatSystemConfiguration {
  readonly capabilities = COMPATIBILITY_CAPABILITIES
  private config: GaussianSplatRenderConfig

  constructor(initialConfig: Partial<GaussianSplatRenderConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...initialConfig }
    this.validateSortingMode(this.config.sortingMode)
    reportGSplatSortingFallbackOnce('webgl2', this.config.sortingMode, 'cpu')
  }

  getConfig(): GaussianSplatRenderConfig {
    return { ...this.config }
  }

  getResolvedSortingMode(): 'cpu' {
    // 迁移期 WebGL manager 仍拥有其冻结 sorter；System 对外只把它视为 CPU compatibility path。
    return 'cpu'
  }

  setConfig(config: Partial<GaussianSplatRenderConfig>): void {
    const next = { ...this.config, ...config }
    this.validateSortingMode(next.sortingMode)
    if (config.maxSplats !== undefined) {
      validateMaxSplats(config.maxSplats)
      next.maxSplats = config.maxSplats
    }
    this.config = next
    reportGSplatSortingFallbackOnce('webgl2', next.sortingMode, 'cpu')
  }

  private validateSortingMode(value: unknown): void {
    if (value !== 'auto' && value !== 'cpu' && value !== 'gpu') {
      throw new Error(`不支持的 GSplat sortingMode: ${String(value)}`)
    }
  }
}

class CompatibilityHostServices implements GSplatSystemHostServices {
  private disposed = false

  constructor(
    private readonly manager: CompatibilityManager,
    private readonly cleanups: Array<() => void>
  ) {}

  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean {
    return this.manager.setClipVolumes?.(volumes.filter(isGlobalGSplatClipVolume)) ?? false
  }

  setLodConfig(config: Partial<LODConfig>): void {
    this.manager.setConfig(config)
  }

  getLodConfig(): LODConfig {
    return this.manager.getConfig()
  }

  getLodStats(): LODStats {
    return this.manager.getStats()
  }

  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics {
    return this.manager.getDeviceBudgetDiagnostics()
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot | undefined {
    return this.manager.getVisualActivitySnapshot?.()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (let index = this.cleanups.length - 1; index >= 0; index--) this.cleanups[index]()
    this.cleanups.length = 0
    this.manager.dispose()
  }
}

class CompatibilityDirector implements GSplatSystemDirector {
  private lastPrepared: { frameNumber: number; viewId: ViewId } | null = null

  constructor(private readonly manager: CompatibilityManager) {}

  updateStreaming(): void {}

  prepareView(frame: ViewFrameContext): void {
    if (
      this.lastPrepared?.frameNumber === frame.frameNumber &&
      this.lastPrepared.viewId === frame.viewId
    ) {
      return
    }
    this.manager.update(
      frame.camera,
      frame.manualFrame?.deterministic
        ? { effectDelta: 0, logicalFrameId: frame.frameNumber, logicalFrameSource: this }
        : {
            effectDelta: frame.delta,
            logicalFrameId: frame.frameNumber,
            logicalFrameSource: this,
          }
    )
    this.lastPrepared = { frameNumber: frame.frameNumber, viewId: frame.viewId }
  }

  suspendView(): void {}

  retireView(): void {}

  getMaterial(): null {
    return null
  }

  dispose(): void {
    this.lastPrepared = null
  }
}

class CompatibilityRenderSubmission implements GSplatSystemRenderSubmission {
  constructor(
    private readonly scene: THREE.Scene,
    private readonly root: THREE.Object3D
  ) {}

  render(
    frame: ViewFrameContext,
    context: PhaseRenderContext,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): void {
    if (!assets.some(({ handle, mounted }) => mounted && handle.visible)) return
    const renderer = context.renderer
    const savedTarget = renderer.getRenderTarget()
    const savedViewport = renderer.getViewport(new THREE.Vector4())
    const savedScissor = renderer.getScissor(new THREE.Vector4())
    const savedScissorTest = renderer.getScissorTest()
    // WebGPU 的 outputTarget 固定为 null；capture session 已把 renderer 绑定到 backend-owned
    // target，这里必须保留当前 target，否则采集会写回默认 surface，读回得到黑帧。
    renderer.setRenderTarget(
      context.graphics.kind === 'webgl2' ? context.outputTarget : savedTarget
    )
    renderer.setViewport(
      context.viewport.x,
      context.viewport.y,
      context.viewport.width,
      context.viewport.height
    )
    renderer.setScissor(
      context.viewport.x,
      context.viewport.y,
      context.viewport.width,
      context.viewport.height
    )
    renderer.setScissorTest(true)
    const restore = this.attachRoot()
    try {
      renderer.render(this.scene, frame.camera)
    } finally {
      restore()
      renderer.setViewport(savedViewport.x, savedViewport.y, savedViewport.z, savedViewport.w)
      renderer.setScissor(savedScissor.x, savedScissor.y, savedScissor.z, savedScissor.w)
      renderer.setScissorTest(savedScissorTest)
      renderer.setRenderTarget(savedTarget)
    }
  }

  dispose(): void {}

  private attachRoot(): () => void {
    const originalParent = this.root.parent
    const originalIndex = originalParent?.children.indexOf(this.root) ?? -1
    this.root.removeFromParent()
    this.scene.add(this.root)
    return () => {
      this.root.removeFromParent()
      if (!originalParent) return
      originalParent.add(this.root)
      const restoredIndex = originalParent.children.indexOf(this.root)
      if (originalIndex >= 0 && restoredIndex >= 0 && restoredIndex !== originalIndex) {
        originalParent.children.splice(restoredIndex, 1)
        originalParent.children.splice(
          Math.min(originalIndex, originalParent.children.length),
          0,
          this.root
        )
      }
    }
  }
}

function getPickOptions(query: PickQuery): Record<string, unknown> {
  const viewRect = query.viewport ?? query.viewRect
  return {
    interactionType: query.interactionType,
    centerSphereRadius: query.centerSphereRadius,
    windowSize: query.windowSize,
    gpuOnly: query.gpuOnly,
    prewarmGpu: query.prewarmGpu,
    screenX: query.screen && viewRect ? query.screen.x - viewRect.x : query.screen?.x,
    screenY: query.screen && viewRect ? query.screen.y - viewRect.y : query.screen?.y,
    screenWidth: viewRect?.width,
    screenHeight: viewRect?.height,
    layerMask: query.layerMask,
  }
}

function resolvePickAsset(
  raw: Record<string, unknown>,
  assets: readonly GSplatSystemAssetSnapshot[],
  manager: CompatibilityManager
): GSplatSystemAssetSnapshot | undefined {
  if (typeof raw.assetId === 'string') return assets.find(({ id }) => id === raw.assetId)
  const splatNode = (raw.splatInfo as { node?: unknown } | undefined)?.node
  if (!splatNode) return undefined
  return assets.find(({ id }) => {
    const model = manager.getByNodeId(id)
    if (model === splatNode) return true
    if (typeof model !== 'object' || model === null) return false
    const candidate = model as { rootNode?: unknown; mesh?: unknown }
    return candidate.rootNode === splatNode || candidate.mesh === splatNode
  })
}

function pickSample(
  manager: CompatibilityManager,
  query: PickQuery,
  assets: readonly GSplatSystemAssetSnapshot[]
): GSplatPickSample | null {
  const rawValue = manager.pick?.(query.ray, query.camera, getPickOptions(query))
  if (typeof rawValue !== 'object' || rawValue === null) return null
  const raw = rawValue as Record<string, unknown>
  const location = raw.location as { x: number; y: number; z: number } | undefined
  if (!location) return null
  const asset = resolvePickAsset(raw, assets, manager)
  if (!asset) return null
  const point =
    location instanceof THREE.Vector3
      ? location.clone()
      : new THREE.Vector3(location.x, location.y, location.z)
  const normalValue = raw.normal as { x: number; y: number; z: number } | undefined
  const normal = normalValue
    ? normalValue instanceof THREE.Vector3
      ? normalValue.clone()
      : new THREE.Vector3(normalValue.x, normalValue.y, normalValue.z)
    : undefined
  return {
    assetId: asset.id,
    resourceGeneration: asset.generation,
    point,
    distance: typeof raw.distance === 'number' ? raw.distance : undefined,
    normal,
    raw,
  }
}

class CompatibilityCpuPicker implements SplatDataCpuPicker {
  constructor(private readonly manager: CompatibilityManager) {}

  pick(query: PickQuery, assets: readonly GSplatSystemAssetSnapshot[]): GSplatPickSample | null {
    return pickSample(this.manager, query, assets)
  }

  dispose(): void {}
}

class CompatibilityGpuPickCoordinator implements GSplatGpuPickCoordinator {
  constructor(private readonly manager: CompatibilityManager) {}

  request(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<GSplatPickSample | null> {
    return Promise.resolve(pickSample(this.manager, query, assets))
  }

  prepareView(): void {}

  cancelAsset(): void {}

  cancelView(): void {}

  dispose(): void {}
}

class CompatibilityBinding implements IGSplatHandleBinding {
  readonly handle: GaussianSplatHandle
  private resourceValue: GSplatResourceLease
  private generationValue: number
  private nextGeneration: number
  private mountedValue = false
  private visibleValue = true
  private enabledValue = true
  private urlValue: string
  private nameValue: string
  private customBoundingBox: THREE.Box3 | null = null
  private disposed = false

  constructor(
    private readonly manager: CompatibilityManager,
    private readonly root: THREE.Object3D,
    private readonly input: GSplatHandleBindingCreateInput,
    private readonly onActivity: (reason: string, id?: AssetId) => void,
    private readonly configuration: GSplatSystemConfiguration
  ) {
    this.resourceValue = input.resource
    this.generationValue = input.generation
    this.nextGeneration = input.generation
    this.urlValue = input.request.url
    this.nameValue = input.state?.name ?? input.request.name ?? input.request.url
    if (input.state) {
      this.visibleValue = input.state.visible
      this.enabledValue = input.state.enabled
      this.mountedValue = input.state.mounted
      this.customBoundingBox = input.state.customAabb?.clone() ?? null
    }
    this.handle = this.createHandle()
    if (this.mountedValue) this.attachObject()
  }

  get resource(): GSplatResourceLease {
    return this.resourceValue
  }

  get generation(): number {
    return this.generationValue
  }

  get mounted(): boolean {
    return this.mountedValue
  }

  mount(): void {
    this.assertActive()
    if (this.mountedValue) return
    this.mountedValue = true
    this.attachObject()
    this.onActivity('gsplat.mounted', this.input.id)
  }

  unmount(): void {
    if (this.disposed || !this.mountedValue) return
    this.resourceValue.object3D?.removeFromParent()
    this.mountedValue = false
    this.onActivity('gsplat.unmounted', this.input.id)
  }

  setVisibility(visible: boolean): void {
    this.assertActive()
    this.visibleValue = visible
    if (this.resourceValue.object3D) this.resourceValue.object3D.visible = visible
    this.manager.setVisible?.(this.input.id, visible)
    this.onActivity('gsplat.visibility', this.input.id)
  }

  setEnabled(enabled: boolean): void {
    this.assertActive()
    this.enabledValue = enabled
    if (this.resourceValue.object3D)
      this.resourceValue.object3D.visible = enabled && this.visibleValue
  }

  setLodConfiguration(): void {
    throw new UnsupportedGraphicsCapabilityError(
      'gsplat-handle:setLodConfiguration',
      'webgl2',
      'PlayCanvas compatibility binding 尚未迁移 per-placement LOD 配置'
    )
  }

  snapshotState(): GSplatHandleBindingState {
    return Object.freeze({
      name: this.nameValue,
      visible: this.visibleValue,
      enabled: this.enabledValue,
      sceneAttached: true,
      mounted: this.mountedValue,
      layerIds: DEFAULT_LAYER_IDS,
      castShadows: false,
      lodBaseDistance: 5,
      lodMultiplier: 3,
      lodRangeMin: 0,
      lodRangeMax: 99,
      customAabb: this.customBoundingBox?.clone() ?? null,
      parameters: new Map(),
      workBufferModifier: null,
      workBufferUpdateMode: 'auto',
    })
  }

  beginResourceReplacement(): number {
    this.assertActive()
    return ++this.nextGeneration
  }

  replaceResource(input: {
    readonly token: number
    readonly resource: GSplatResourceLease
    readonly source: GSplatBindingResourceSource
    readonly url?: string
    readonly name?: string
  }): boolean {
    if (this.disposed || input.token !== this.nextGeneration) {
      input.resource.dispose()
      return false
    }
    const previous = this.resourceValue
    previous.object3D?.removeFromParent()
    this.resourceValue = input.resource
    this.generationValue = input.token
    if (input.url !== undefined) this.urlValue = input.url
    if (input.name !== undefined) this.nameValue = input.name
    if (this.mountedValue) this.attachObject()
    previous.dispose()
    this.onActivity('gsplat.resourceReplaced', this.input.id)
    return true
  }

  dispose(): void {
    if (this.disposed) return
    this.unmount()
    this.disposed = true
    this.generationValue = ++this.nextGeneration
    this.resourceValue.dispose()
  }

  private createHandle(): GaussianSplatHandle {
    const binding = this
    const id = this.input.id
    return {
      id,
      kind: 'gaussian-splat',
      get sortingMode() {
        return binding.configuration.getConfig().sortingMode
      },
      get resolvedSortingMode() {
        return binding.configuration.getResolvedSortingMode()
      },
      get url() {
        return binding.urlValue
      },
      get name() {
        return binding.nameValue
      },
      set name(value: string) {
        binding.nameValue = value
      },
      get visible() {
        return binding.visibleValue
      },
      set visible(value: boolean) {
        binding.setVisibility(value)
      },
      get boundingBox() {
        return binding.customBoundingBox?.clone() ?? binding.resourceValue.boundingBox
      },
      set boundingBox(value: THREE.Box3 | null) {
        binding.customBoundingBox = value?.clone() ?? null
      },
      get object3D() {
        return binding.resourceValue.object3D
      },
      get numSplats() {
        return binding.resourceValue.numSplats
      },
      get lodStats() {
        return binding.manager.getStats()
      },
      get environmentConfigured() {
        return binding.manager.getEnvironmentVisible?.(id) !== undefined
      },
      get environmentVisible() {
        return binding.manager.getEnvironmentVisible?.(id) ?? false
      },
      setEnvironmentVisible(visible: boolean) {
        return binding.apply(
          'gsplat.environment',
          () => binding.manager.setEnvironmentVisible?.(id, visible) ?? false
        )
      },
      setScalarFilter(options) {
        return binding.apply('gsplat.scalarFilter', () =>
          binding.manager.setScalarFilter(id, options)
        )
      },
      clearScalarFilter() {
        return binding.apply('gsplat.scalarFilter', () => binding.manager.clearScalarFilter(id))
      },
      getScalarHistogram(options) {
        return binding.manager.getScalarHistogram(id, options)
      },
      setCentersDisplayMode(mode) {
        return binding.apply(
          'gsplat.centersDisplay',
          () => binding.manager.setCentersDisplayMode?.(id, mode) ?? false
        )
      },
      setBoxHelperVisible(visible) {
        binding.manager.setBoxHelperVisible(visible, [id])
        binding.onActivity('gsplat.boxHelper', id)
      },
      setCentersStyle(style) {
        return binding.apply(
          'gsplat.centersStyle',
          () => binding.manager.setCentersStyle?.(id, style) ?? false
        )
      },
      setColorSpaceConfig(config) {
        binding.manager.setColorSpaceConfig?.(config)
        binding.onActivity('gsplat.colorSpace', id)
      },
      setColorAdjustment(options) {
        return binding.apply(
          'gsplat.colorAdjustment',
          () => binding.manager.setColorAdjustment?.(id, options) ?? false
        )
      },
      getColorAdjustment() {
        return binding.manager.getColorAdjustment?.(id)
      },
      resetColorAdjustment() {
        return binding.apply(
          'gsplat.colorAdjustment',
          () => binding.manager.resetColorAdjustment?.(id) ?? false
        )
      },
      setShaderEffect(effect) {
        return binding.apply(
          'gsplat.shaderEffect',
          () => binding.manager.setShaderEffect?.(id, effect) ?? false
        )
      },
      clearShaderEffect() {
        return binding.apply(
          'gsplat.shaderEffect',
          () => binding.manager.clearShaderEffect?.(id) ?? false
        )
      },
      restartShaderEffect() {
        return binding.apply(
          'gsplat.shaderEffect',
          () => binding.manager.restartShaderEffect?.(id) ?? false
        )
      },
      setShaderEffectUniform(name, value) {
        return binding.apply(
          'gsplat.shaderEffectUniform',
          () => binding.manager.setShaderEffectUniform?.(id, name, value) ?? false
        )
      },
      setWorkBufferModifier(modifier) {
        return binding.apply(
          'gsplat.workBuffer',
          () => binding.manager.setWorkBufferModifier?.(id, modifier) ?? false
        )
      },
      setWorkBufferUpdateMode(mode) {
        return binding.apply(
          'gsplat.workBuffer',
          () => binding.manager.setWorkBufferUpdateMode?.(id, mode) ?? false
        )
      },
      setSceneOrigin(offset) {
        return binding.apply(
          'gsplat.sceneOrigin',
          () => binding.manager.setSceneOrigin?.(id, offset) ?? false
        )
      },
    }
  }

  private attachObject(): void {
    if (!this.enabledValue || !this.visibleValue) return
    const object3D = this.resourceValue.object3D
    if (!object3D) return
    setMaskRecursive(object3D, object3D.layers.mask)
    this.root.add(object3D)
  }

  private apply(reason: string, operation: () => boolean): boolean {
    const applied = operation()
    if (applied) this.onActivity(reason, this.input.id)
    return applied
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('PlayCanvas compatibility binding 已销毁')
  }
}

class CompatibilityBindingFactory implements IGSplatHandleBindingFactory {
  constructor(
    private readonly manager: CompatibilityManager,
    private readonly root: THREE.Object3D,
    private readonly onActivity: (reason: string, id?: AssetId) => void,
    private readonly configuration: GSplatSystemConfiguration
  ) {}

  create(input: GSplatHandleBindingCreateInput): IGSplatHandleBinding {
    return new CompatibilityBinding(
      this.manager,
      this.root,
      input,
      this.onActivity,
      this.configuration
    )
  }
}

interface LoaderDefinition {
  readonly format: string
  readonly detectPriority: number
  readonly pattern: RegExp
}

const LOADERS: readonly LoaderDefinition[] = Object.freeze([
  { format: 'lod-meta', detectPriority: 10, pattern: /(?:^|[/\\])lod-meta\.json$/i },
  { format: 'sog-json', detectPriority: 6, pattern: /(?:^|[/\\])meta\.json$/i },
  { format: 'sog', detectPriority: 5, pattern: /\.sog$/i },
  { format: 'splat', detectPriority: 3, pattern: /\.splat$/i },
  { format: 'ply', detectPriority: 0, pattern: /\.ply$/i },
])

function createManagerLoader(
  definition: LoaderDefinition,
  manager: CompatibilityManager,
  configuration: GSplatSystemConfiguration
): AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease> {
  return {
    format: definition.format,
    detectPriority: definition.detectPriority,
    canLoad: ({ url }) => definition.pattern.test(getGSplatUrlPath(url)),
    load: async (request, context) => {
      const id = request.id
      if (!id) throw new Error('GSplat compatibility loader 缺少 System 分配的 asset id')
      const result = await manager.load(request.url, {
        ...request.options,
        signal: context.signal ?? request.signal,
        nodeId: id,
      })
      context.onProgress?.(1)
      if (context.signal?.aborted) {
        manager.removeByNodeId(id)
        throw new DOMException('Aborted', 'AbortError')
      }
      const limit = configuration.getConfig().maxSplats
      // 0 = 不限制：由调用端按设备预算自行设置上限。
      if (limit > 0 && result.numSplats > limit) {
        manager.removeByNodeId(id)
        throw new Error(
          `GSplatSystem: splatCount 超出 maxSplats，actual=${result.numSplats}, limit=${limit}`
        )
      }
      const object3D = extractObject3D(manager, id)
      return createCompatibilityResource({
        id,
        boundingBox: toBoundingBox(result),
        numSplats: result.numSplats,
        object3D,
        cpuData: getCpuData(manager, id),
        resourceKind: definition.format === 'lod-meta' ? 'octree' : 'uncompressed',
        streaming: definition.format === 'lod-meta' ? 'lod' : 'static',
        octreeSemantics: definition.format === 'lod-meta' ? 'dataset' : 'none',
        release: () => manager.removeByNodeId(id),
      })
    },
  }
}

function adaptExtraLoader(
  loader: NonNullable<GaussianSplatPluginOptions['extraLoaders']>[number],
  backend: EngineContext['graphics']['kind'],
  capabilities: Pick<GSplatCapability, 'supported'>
): AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease> {
  const guardedLoader = guardGSplatExtraLoaderCapability(loader, backend, capabilities)
  return {
    format: guardedLoader.format,
    detectPriority: guardedLoader.detectPriority,
    canLoad: (request) => guardedLoader.canLoad?.(request) ?? false,
    load: async (request: GaussianSplatLoadRequest, context: LoadContext) => {
      const result = await guardedLoader.load(request, context)
      const declaration = guardedLoader.gsplatCapability
      const resourceKind =
        declaration?.format === 'compressed'
          ? 'compressed'
          : declaration?.format === 'sog'
            ? 'sog'
            : declaration?.streaming === 'lod'
              ? 'octree'
              : 'uncompressed'
      return createCompatibilityResource({
        id: request.id ?? request.url,
        boundingBox: result.metadata.boundingBox,
        numSplats: result.metadata.splatCount,
        object3D: null,
        cpuData: null,
        resourceKind,
        streaming: declaration?.streaming ?? 'static',
        octreeSemantics: declaration?.streaming === 'lod' ? 'dataset' : 'none',
        release: () => result.nodeLoader.dispose(),
      })
    },
  }
}

function createExporter(
  format: string,
  operation: (exporter: SplatExporter, data: SplatData) => Promise<Blob>
): AssetExporter<GSplatExportSource, Blob> & {
  readonly gsplatCapability: GSplatExtraExporterCapability
} {
  const exporter = new SplatExporter()
  return {
    format,
    gsplatCapability: {
      resource: 'GSplatResource',
      format: 'uncompressed',
      streaming: 'static',
      octree: 'none',
    },
    canExport: ({ cpuData, resourceKind, streaming, octreeSemantics }) =>
      resourceKind === 'uncompressed' &&
      streaming === 'static' &&
      octreeSemantics === 'none' &&
      cpuData instanceof SplatData,
    export: ({ cpuData, resourceKind, streaming, octreeSemantics }) => {
      if (
        resourceKind !== 'uncompressed' ||
        streaming !== 'static' ||
        octreeSemantics !== 'none' ||
        !(cpuData instanceof SplatData)
      ) {
        throw new Error(`GSplatSystem: ${format} exporter 只接受静态 uncompressed SplatData`)
      }
      return operation(exporter, cpuData as SplatData)
    },
  }
}

function adaptExtraExporter(
  exporter: NonNullable<GaussianSplatPluginOptions['extraExporters']>[number]
): AssetExporter<GSplatExportSource, Blob> & {
  readonly gsplatCapability?: GSplatExtraExporterCapability
} {
  return {
    format: exporter.format,
    gsplatCapability: (exporter as GaussianSplatExtraExporter).gsplatCapability,
    canExport: ({ handle }) => exporter.canExport?.(handle) ?? true,
    export: ({ handle }, options?: ExportOptions) => exporter.export(handle, options),
  }
}

function createBuiltInExporters(
  options: Readonly<GaussianSplatPluginOptions>
): AssetExporter<GSplatExportSource, Blob>[] {
  const exporters: AssetExporter<GSplatExportSource, Blob>[] = [
    createExporter('ply', (exporter, data) => exporter.exportPly(data)),
    createExporter('splat', (exporter, data) => exporter.exportSplat(data)),
    createExporter('compressed-ply', (exporter, data) => exporter.exportCompressedPly(data)),
  ]
  for (const exporter of options.extraExporters ?? []) exporters.push(adaptExtraExporter(exporter))
  return exporters
}

export interface GaussianSplatSystemFactoryInput {
  readonly context: EngineContext
  readonly renderScene: THREE.Scene
  readonly implementationLineage: 'playcanvas'
  readonly options: Readonly<GaussianSplatPluginOptions>
  readonly initialConfig?: Partial<GaussianSplatRenderConfig>
  readonly onVisualActivity: GSplatVisualActivityCallback
  readonly onLodError: (data: LODEventData['loadError']) => void
}

/**
 * System -> Plugin 的视觉活动通知。
 *
 * `id` 保留现有 asset 诊断语义，`viewId` 只用于把 per-View 的待处理工作定向唤醒；
 * 两者不能混用。省略 `viewId` 表示全局活动，兼容旧 Manager/loader 回调。
 */
export type GSplatVisualActivityCallback = (reason: string, id?: AssetId, viewId?: ViewId) => void

export interface GaussianSplatSystemFactory {
  create(input: GaussianSplatSystemFactoryInput): GSplatSystem
}

/** preload 动态模块向默认 factory 注入的 WebGPU 激活入口。 */
export type WebGPUGSplatSystemActivator = (
  input: GaussianSplatSystemFactoryInput,
  exporters: readonly AssetExporter<GSplatExportSource, Blob>[]
) => GSplatSystem

/**
 * 7.11 迁移组合：Plugin 已只拥有一个 GSplatSystem，冻结 WebGL manager 仅作为 System 内部可删除实现。
 * 7.15 会把 loaders/binding/director 换成共享 Resource/Placement/World，而不再改 Plugin 公共对象模型。
 */
export class DefaultGaussianSplatSystemFactory implements GaussianSplatSystemFactory {
  constructor(
    private readonly readWebGPUActivator: () => WebGPUGSplatSystemActivator | null = () => null
  ) {}

  create(input: GaussianSplatSystemFactoryInput): GSplatSystem {
    if (input.context.graphics.kind === 'webgpu' && (input.options.extraLoaders?.length ?? 0) > 0) {
      throw new UnsupportedGraphicsCapabilityError(
        'webgpu-activation:gsplat-extra-loaders',
        input.context.graphics.kind,
        'WebGPU 不会回退到 WebGL compatibility manager；自定义 loader 将由 capability 迁移任务接入'
      )
    }

    if (input.context.graphics.kind === 'webgpu') {
      const activate = this.readWebGPUActivator()
      if (!activate) {
        throw new UnsupportedGraphicsCapabilityError(
          'webgpu-activation:gsplat-preload',
          input.context.graphics.kind,
          'GaussianSplatPlugin.preload 尚未完成 WebGPU 图形模块激活'
        )
      }
      const exporters = createBuiltInExporters(input.options)
      return activate(input, exporters)
    }

    const adapter: GSplatGraphicsAdapter = new WebGLGSplatGraphicsAdapterFactory().create(
      input.context.graphics
    )
    if ((input.options.extraLoaders?.length ?? 0) === 0) {
      const exporters = createBuiltInExporters(input.options)
      try {
        return createWebGLGSplatUnifiedSystem({
          context: input.context,
          adapter: adapter as WebGLGSplatGraphicsAdapter,
          options: input.options,
          initialConfig: input.initialConfig,
          exporters,
          onVisualActivity: input.onVisualActivity,
        })
      } catch (error) {
        adapter.dispose()
        throw error
      }
    }
    let manager: CompatibilityManager
    try {
      manager = createGS3DManager({
        engine: input.implementationLineage,
        scene: input.renderScene,
        renderer: input.context.renderer,
        lodConfig: input.options.lodConfig,
        withCredentials: input.options.withCredentials ?? false,
      }) as CompatibilityManager
    } catch (error) {
      adapter.dispose()
      throw error
    }
    const cleanups: Array<() => void> = []
    try {
      const configuration = new CompatibilityConfiguration(input.initialConfig)
      const bind = (event: LODEventType, reason: string) => {
        const handler = () => input.onVisualActivity(reason)
        manager.on(event, handler)
        cleanups.push(() => manager.off(event, handler))
      }
      bind('loadProgress', 'gsplat.lodMetaReady')
      bind('nodeLoaded', 'gsplat.lodReady')
      bind('nodeGpuReady', 'gsplat.lodGpuReady')
      bind('nodeUnloaded', 'gsplat.lodUnloaded')
      bind('sortReady', 'gsplat.sortReady')
      const loadErrorHandler = (data: LODEventData['loadError']) => {
        input.onVisualActivity('gsplat.lodError')
        input.onLodError(data)
      }
      manager.on('loadError', loadErrorHandler)
      cleanups.push(() => manager.off('loadError', loadErrorHandler))

      const loaders: AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease>[] = LOADERS.map(
        (definition) => createManagerLoader(definition, manager, configuration)
      )
      for (const loader of input.options.extraLoaders ?? []) {
        loaders.push(
          adaptExtraLoader(loader, input.context.graphics.kind, configuration.capabilities)
        )
      }

      const exporters: AssetExporter<GSplatExportSource, Blob>[] = [
        createExporter('ply', (exporter, data) => exporter.exportPly(data)),
        createExporter('splat', (exporter, data) => exporter.exportSplat(data)),
        createExporter('compressed-ply', (exporter, data) => exporter.exportCompressedPly(data)),
      ]
      for (const exporter of input.options.extraExporters ?? []) {
        exporters.push(adaptExtraExporter(exporter))
      }

      return new GSplatSystem({
        adapter,
        bindingFactory: new CompatibilityBindingFactory(
          manager,
          input.context.sceneGraph.gaussianSplatRoot,
          input.onVisualActivity,
          configuration
        ),
        director: new CompatibilityDirector(manager),
        configuration,
        cpuPicker: new CompatibilityCpuPicker(manager),
        gpuPickCoordinator: new CompatibilityGpuPickCoordinator(manager),
        renderSubmission: new CompatibilityRenderSubmission(
          input.renderScene,
          input.context.sceneGraph.gaussianSplatRoot
        ),
        loaders,
        exporters,
        hostServices: new CompatibilityHostServices(manager, cleanups),
      })
    } catch (error) {
      for (let index = cleanups.length - 1; index >= 0; index--) cleanups[index]()
      manager.dispose()
      adapter.dispose()
      throw error
    }
  }
}
